import React from "react";

type PickerLayout = "expanded" | "wrap" | "flexible";

export type PickerColors = {
  bg?: string;
  text?: string;
  border?: string;
  shadow?: string;
};

export interface SegmentedPickerProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;

  ariaLabel?: string;
  layout?: PickerLayout;

  /**
   * Used by wrap/flexible layouts.
   */
  minButtonWidth?: number;

  selectedColor?: PickerColors;
  unselectedColor?: PickerColors;

  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
}

const defaultSelected = {
  bg: "#EEF2FF",
  text: "#4F46E5",
  border: "#EEF2FF",
  shadow: "#C7D2FE",
};

const defaultUnselected = {
  bg: "#FFFFFF",
  text: "#6B7280",
  border: "#FFFFFF",
  shadow: "transparent",
};

const getLayoutClassName = (layout: PickerLayout) => {
  switch (layout) {
    case "expanded":
      return "grid grid-flow-col auto-cols-fr";
    case "wrap":
      return "flex flex-wrap";
    case "flexible":
      return "flex flex-wrap";
    default:
      return "flex flex-wrap";
  }
};

const getButtonLayoutClassName = (layout: PickerLayout) => {
  switch (layout) {
    case "expanded":
      return "w-full";
    case "wrap":
      return "flex-none";
    case "flexible":
      return "flex-1";
    default:
      return "flex-1";
  }
};

export const SegmentedPicker: React.FC<SegmentedPickerProps> = ({
  options,
  value,
  onChange,
  ariaLabel = "Choose an option",
  layout = "flexible",
  minButtonWidth = 160,
  selectedColor = {},
  unselectedColor = {},
  disabled = false,
  className = "",
  buttonClassName = "",
}) => {
  const selected = { ...defaultSelected, ...selectedColor };
  const unselected = { ...defaultUnselected, ...unselectedColor };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={[getLayoutClassName(layout), "w-full gap-3", className].join(
        " ",
      )}
    >
      {options.map((option) => {
        const isSelected = option === value;
        const colors = isSelected ? selected : unselected;

        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={disabled}
            onClick={() => onChange(option)}
            style={{
              minWidth: layout === "expanded" ? undefined : minButtonWidth,
              backgroundColor: colors.bg,
              color: colors.text,
              borderColor: colors.border,
              boxShadow: isSelected
                ? `0 6px 0 ${colors.shadow}`
                : `0 0 0 ${colors.shadow}`,
            }}
            className={[
              getButtonLayoutClassName(layout),
              "relative min-h-[56px] rounded-[1.4rem] border-2 px-6 py-3",
              "text-center text-base font-black tracking-wide",
              "transition-all duration-150 ease-out",
              "hover:-translate-y-0.5",
              "active:translate-y-1 active:shadow-none",
              "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#84D8FF]/50",
              "disabled:pointer-events-none disabled:opacity-50",
              !isSelected && "hover:bg-[#F7F7F7]",
              buttonClassName,
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <span className="block truncate">{option}</span>
          </button>
        );
      })}
    </div>
  );
};
